import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SoftwareTypeSummary from "./SoftwareTypeSummary";
import DeviceScanSummary from "./DeviceScanSummary";
import DeviceOsSummary from "../devices/DeviceOsSummary";
import DeviceCompliance from "../devices/DeviceCompliance";
import SoftwareTypeTrend from "./SoftwareTypeTrend";
import SoftwareLicenseSummary from "./SoftwareLicenseSummary";
import RecentlyInstalledSummary from "./RecentlyInstalledSummary";
import API from "common-ui-components/components/utils/API";
import DeviceType from "../devices/DeviceType";
import OverviewCards from "./OverviewCards";
import useSplitOverviewData from "./useSplitOverviewData";
import styles from "./OverviewCards.module.css";

const Overview = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [overviewData, setOverviewData] = useState({});
  const [loading, setLoading] = useState(true);
  // The Home dashboard is hosted at /app/home/inventory, so relative
  // navigation from it cannot resolve the inventory software route.
  const softwareAppsPath = embedded ? "/app/inventory/apps" : "../apps";

  // A reusable function to fetch overview data from the API
  const getOverviewData = async () => {
    setLoading(true);
    try {
      const response = await API.get("/inventory/getOverview");
      setOverviewData(response);
    } catch (error) {
      console.error("Error fetching overview data: ", error);
    }
    setLoading(false);
  };

  // Fetch data on initial component mount
  useEffect(() => {
    getOverviewData();
  }, []);

  const {
    overviewCardsData,
    deviceOsSummaryData,
    deviceTypeSummaryData,
    softwareTypeSummaryData,
    softwareTypeTrendData,
    softwareLicenseSummaryData,
    deviceComplianceData,
  } = useSplitOverviewData(overviewData);

  const navigateWithFilter = (path, activeFilters) => {
    navigate(path, {
      state: {
        active_filters: activeFilters,
        activeFiltersKey: Date.now(),
      },
    });
  };

  const handleOsFilterSelect = ({ id }) => {
    navigateWithFilter("../devices", {
      comprehensive_filters: [{ column: "platform", value: [id] }],
    });
  };

  const handleTypeFilterSelect = ({ id }) => {
    const typeValueMap = {
      Desktop: "desktop",
      Tablet: "tablet",
      Server: "server",
      Laptop: "laptop",
      Others: "other",
    };

    navigateWithFilter("../devices", {
      comprehensive_filters: [
        { column: "deviceTypeCategory", value: [typeValueMap[id] || id] },
      ],
    });
  };

  const handleDeviceComplianceFilterSelect = ({ id }) => {
    const complianceValueMap = {
      Compliance: "Compliant",
      "Non Compliance": "Non Compliant",
      "Not Evaluated": "Not Evaluated",
    };

    navigateWithFilter("../devices", {
      comprehensive_filters: [
        { column: "compliance", value: [complianceValueMap[id] || id] },
      ],
    });
  };

  const handleSoftwareTypeFilterSelect = ({ id }) => {
    const softwareTypeFilterMap = {
      Commercial: { column: "licenseType", value: [0] },
      "Non Commercial": { column: "licenseType", value: [1] },
      Prohibited: { column: "accessType", value: [1] },
      "Commercial SW": { column: "licenseType", value: [0] },
      "Prohibited SW": { column: "accessType", value: [1] },
    };

    const filter = softwareTypeFilterMap[id];
    if (!filter) return;

    navigateWithFilter(softwareAppsPath, {
      comprehensive_filters: [filter],
    });
  };

  return (
    <div
      className={styles.overview}
    >
      {/* Pass the getOverviewData function to the child component as a prop */}
      <OverviewCards
        cardsData={overviewCardsData}
        onRefreshComplete={getOverviewData}
        hideHeader={embedded}
        loading={loading}
      />
      {/* {loading && <ResponseLoader/>} */}
      <div
        className={`${styles.cardsRow} ${styles.deviceRow} hideScrollbar`}
      >
        <DeviceOsSummary
          osSummary={deviceOsSummaryData}
          onFilterSelect={embedded ? undefined : handleOsFilterSelect}
          className={styles.deviceChartCard}
        />
        <DeviceType
          typeSummary={deviceTypeSummaryData}
          onFilterSelect={embedded ? undefined : handleTypeFilterSelect}
          className={styles.deviceChartCard}
        />
        <DeviceCompliance
          complianceData={deviceComplianceData}
          onFilterSelect={
            embedded ? undefined : handleDeviceComplianceFilterSelect
          }
          className={styles.deviceChartCard}
          chartSize="12rem"
          chartGap="0.35rem"
          legendPadding="0"
        />
      </div>
      <div
        className={`${styles.cardsRow} ${styles.softwareRow} hideScrollbar`}
      >
        <SoftwareTypeSummary
          typeSummary={softwareTypeSummaryData}
          onFilterSelect={handleSoftwareTypeFilterSelect}
          onViewSoftware={() => navigate(softwareAppsPath)}
          loading={loading}
        />
        <DeviceScanSummary
          successCount={overviewData.scanSuccessCount}
          failedCount={overviewData.scanFailedCount}
          loading={loading}
          onViewAll={() => navigate("../settings/scan-devices")}
        />
      </div>
      <div
        className={`${styles.cardsRow} ${styles.trendRow} hideScrollbar`}
      >
        <SoftwareTypeTrend trendData={softwareTypeTrendData} onFilterSelect={handleSoftwareTypeFilterSelect} />
        <SoftwareLicenseSummary licenseSummary={softwareLicenseSummaryData} />
      </div>
      <div className={`${styles.cardsRow} ${styles.lowerRow}`}>
        <RecentlyInstalledSummary />
      </div>
    </div>
  );
};

export default Overview;
